import React from 'react';

import type {
  CallReadyRangeVisual,
  CallReadyRangeVisualRow,
  CallReadyVisualEmphasis,
} from '../../../lib/findings/callReadyFindings';
import { cn } from '../../../lib/utils';

export interface AnswerRangeRailProps {
  visual: CallReadyRangeVisual;
}

export interface RangeScale {
  /** Right edge of the rail. Always a tick. */
  maximum: number;
  /** Tick positions from 0 to `maximum`, inclusive, at a nice step. */
  ticks: readonly number[];
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function roundToStep(value: number, step: number): number {
  return Number((Math.round(value / step) * step).toPrecision(12));
}

/** 1 / 2 / 2.5 / 5 × 10ⁿ, the smallest at or above `rough`. */
function niceStep(rough: number): number {
  const exponent = Math.floor(Math.log10(rough));
  const base = 10 ** exponent;
  const fraction = rough / base;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return Number((nice * base).toPrecision(12));
}

/**
 * A rail runs from 0 to a nice maximum a little above the largest plotted
 * value, with four or five ticks at a 1/2/2.5/5 step. Rows never define the
 * scale on their own: the same rule gives every visual comparable rulers.
 */
export function buildRangeScale(rows: readonly CallReadyRangeVisualRow[]): RangeScale {
  const observed = Math.max(0, ...rows.map((row) => Math.max(row.low, row.high)));
  if (!(observed > 0)) return { maximum: 1, ticks: [0, 0.25, 0.5, 0.75, 1] };
  const padded = observed * 1.05;
  const step = niceStep(padded / 4);
  const maximum = roundToStep(Math.ceil(padded / step) * step, step);
  const ticks: number[] = [];
  for (let tick = 0; tick <= maximum + step / 2; tick += step) ticks.push(roundToStep(tick, step));
  return { maximum, ticks };
}

function tickFormatter(maximum: number): (value: number) => string {
  const formatter = maximum >= 10_000
    ? new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })
    : new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
  return (value) => formatter.format(value);
}

const EMPHASIS_LABEL: Record<CallReadyVisualEmphasis, string> = {
  selected: 'Recorded design',
  alternative: 'Alternative case',
  benchmark: 'Sensitivity or benchmark',
};

const EMPHASIS_BAR_CLASS: Record<CallReadyVisualEmphasis | 'plain', string> = {
  plain: 'border-primary bg-primary/25',
  selected: 'border-primary bg-primary/70',
  alternative: 'border-dashed border-primary bg-primary/10',
  benchmark: 'border-text-muted bg-text-muted/25',
};

const ROW_GRID = 'grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(8rem,0.8fr)_minmax(10rem,1.5fr)_minmax(7rem,auto)]';

/**
 * Compact engineering range graphic used in the Stakeholder Brief.
 * The text values remain authoritative; the rail only makes relative scale,
 * uncertainty, and which row is the recorded design visible at a glance. A
 * ruled axis with a nice step replaces the single right-edge maximum, and
 * rows carry an emphasis (recorded design / alternative / sensitivity) that
 * the legend explains only when the rows differ.
 */
export const AnswerRangeRail: React.FC<AnswerRangeRailProps> = ({ visual }) => {
  const scale = buildRangeScale(visual.rows);
  const formatTick = tickFormatter(scale.maximum);
  const emphasisKinds = [...new Set(visual.rows.map((row) => row.emphasis ?? 'plain'))];
  const showLegend = emphasisKinds.length > 1;

  return (
    <figure
      className="border-y border-border-subtle py-3"
      data-testid={`answer-range-visual-${visual.id}`}
    >
      <figcaption className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-mono type-meta text-text-primary">
          {visual.title}
        </span>
        <span className="font-mono type-meta text-text-muted">{visual.unitLabel}</span>
      </figcaption>

      {visual.callouts && visual.callouts.length > 0 ? (
        <div className="mt-3 grid gap-2" data-testid={`answer-range-callouts-${visual.id}`}>
          {visual.callouts.map((callout) => (
            <div
              key={callout.id}
              className={cn(
                'grid gap-1 border-l-2 px-3 py-2 sm:grid-cols-[minmax(7rem,0.65fr)_minmax(12rem,1fr)] sm:gap-x-3',
                callout.tone === 'caution'
                  ? 'border-warning bg-warning/5'
                  : 'border-primary bg-primary/5',
              )}
            >
              <span className="text-xs font-semibold text-text-primary">{callout.label}</span>
              <span className="font-mono type-meta tabular-nums text-text-primary sm:text-right">{callout.value}</span>
              <p className="type-caption leading-relaxed text-text-muted sm:col-span-2">{callout.note}</p>
            </div>
          ))}
        </div>
      ) : null}

      {visual.rows.length > 0 ? (
        <>
          <div
            className={cn('mt-3 grid items-end gap-x-3', ROW_GRID)}
            aria-hidden="true"
            data-testid={`answer-range-axis-${visual.id}`}
          >
            <span className="hidden sm:block" />
            <div className="relative col-span-2 h-4 border-b border-border-subtle font-mono type-meta tabular-nums text-text-muted sm:col-span-1">
              {scale.ticks.map((tick, index) => {
                const percent = clampPercent((tick / scale.maximum) * 100);
                const last = index === scale.ticks.length - 1;
                return (
                  <span
                    key={tick}
                    className={cn(
                      'absolute top-0 whitespace-nowrap',
                      index === 0 ? '' : last ? '-translate-x-full' : '-translate-x-1/2',
                    )}
                    style={{ left: `${percent}%` }}
                  >
                    {last ? `${formatTick(tick)} ${visual.unitLabel}` : formatTick(tick)}
                  </span>
                );
              })}
            </div>
            <span className="hidden sm:block" />
          </div>

          <ul className="mt-2 space-y-2.5">
            {visual.rows.map((row) => {
              const low = Math.min(row.low, row.high);
              const high = Math.max(row.low, row.high);
              const lowPercent = clampPercent((low / scale.maximum) * 100);
              const highPercent = clampPercent((high / scale.maximum) * 100);
              const isPoint = Math.abs(highPercent - lowPercent) < 0.25;
              const barClass = EMPHASIS_BAR_CLASS[row.emphasis ?? 'plain'];

              return (
                <li
                  key={row.id}
                  className={cn('grid min-w-0 items-center gap-x-3 gap-y-1', ROW_GRID)}
                  aria-label={`${row.label}: ${row.valueLabel}`}
                  data-emphasis={row.emphasis ?? 'plain'}
                >
                  <span className={cn(
                    'min-w-0 text-xs',
                    row.emphasis === 'selected' ? 'font-semibold text-text-primary' : 'font-medium text-text-secondary',
                  )}>
                    {row.label}
                  </span>
                  <div className="relative col-span-2 h-5 sm:col-span-1" aria-hidden>
                    {scale.ticks.map((tick) => (
                      <span
                        key={tick}
                        className="absolute inset-y-0 w-px bg-border-subtle"
                        style={{ left: `${clampPercent((tick / scale.maximum) * 100)}%` }}
                      />
                    ))}
                    <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border-subtle" />
                    <span
                      className={cn(
                        'absolute top-1/2 -translate-y-1/2 border',
                        barClass,
                        isPoint ? 'h-3.5 w-3.5 -translate-x-1/2 rotate-45 rounded-[2px]' : 'h-2.5 min-w-1.5 rounded-sm',
                      )}
                      style={isPoint
                        ? { left: `${lowPercent}%` }
                        : { left: `${lowPercent}%`, width: `${Math.max(0.75, highPercent - lowPercent)}%` }}
                    />
                  </div>
                  <span className="row-start-1 font-mono type-meta tabular-nums text-text-primary sm:row-auto sm:text-right">
                    {row.valueLabel}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {showLegend ? (
        <ul
          className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono type-meta text-text-muted"
          aria-label="Legend"
          data-testid={`answer-range-legend-${visual.id}`}
        >
          {emphasisKinds.map((kind) => (
            <li key={kind} className="inline-flex items-center gap-1.5">
              <span className={cn('inline-block h-2 w-4 border', EMPHASIS_BAR_CLASS[kind])} aria-hidden />
              {kind === 'plain' ? 'Screening range' : EMPHASIS_LABEL[kind]}
            </li>
          ))}
        </ul>
      ) : null}

      {visual.note ? (
        <p className="mt-3 max-w-[72ch] type-caption leading-relaxed text-text-muted">{visual.note}</p>
      ) : null}
    </figure>
  );
};
